import { ViewportObserver } from "../viewport";
import * as E from "~/renderer/web-components/element";
import * as S from "~/styles/sfml.css";
import * as C from "~/constants";

import { WayfinderList } from "./list";
import { WayfinderControl } from "./control";
import { WayfinderCategory } from "./category";

const WAYFINDER_MARGIN = 20;
const WAYFINDER_THUMB = `<svg x="0px" y="0px" viewBox="0 0 20 20" enable-background="new 0 0 20 20" width="16" height="16"><g fill="#428CE8" opacity="1"><g><path fill-rule="evenodd" clip-rule="evenodd" d="M4,9c0.28,0,0.53-0.11,0.71-0.29L10,3.41l5.29,5.29C15.47,8.89,15.72,9,16,9 c0.55,0,1-0.45,1-1c0-0.28-0.11-0.53-0.29-0.71l-6-6C10.53,1.11,10.28,1,10,1S9.47,1.11,9.29,1.29l-6,6C3.11,7.47,3,7.72,3,8 C3,8.55,3.45,9,4,9z M16,11c-0.28,0-0.53,0.11-0.71,0.29L10,16.59l-5.29-5.29C4.53,11.11,4.28,11,4,11c-0.55,0-1,0.45-1,1 c0,0.28,0.11,0.53,0.29,0.71l6,6C9.47,18.89,9.72,19,10,19s0.53-0.11,0.71-0.29l6-6C16.89,12.53,17,12.28,17,12 C17,11.45,16.55,11,16,11z"></path></g></g></svg>`;
const WAYFINDER_HIDE_TIMER = 3000;

export class Wayfinder extends E.SFMLElement implements WayfinderControl {
  private list: WayfinderList;
  private thumb: HTMLElement;
  private thumbInnerTrack: HTMLElement;
  private touchGuard: HTMLElement;

  private wayfinderVisible: boolean = false;

  private _thumbDragging: boolean = false;
  private thumbPointerIdentifier: number | undefined = undefined;
  private thumbDeltaY: number = 0;
  private thumbValue: number = 0;

  private hideWayfinderTimeout: number | null = null;

  public get scrubbing() {
    return this._thumbDragging;
  }

  constructor() {
    super();

    this.setAttribute("sfml-wayfinder", "");

    if (ViewportObserver.shared.iframed) this.positionInIframe();

    this.list = new WayfinderList();
    this.hideWayfinder();

    this.thumb = document.createElement("div");
    this.thumb.className = S.thumb;
    this.thumb.innerHTML = WAYFINDER_THUMB;

    this.thumbInnerTrack = document.createElement("div");
    this.thumbInnerTrack.className = S.thumbInnerTrack;
    this.thumbInnerTrack.appendChild(this.thumb);

    this.touchGuard = document.createElement("div");
    this.touchGuard.className = S.touchGuard;

    this.appendChildToShadow(this.list);
    this.appendChildToShadow(this.thumbInnerTrack);
    this.appendChildToShadow(this.touchGuard);

    this.setScrollPosition(0);
  }

  private positionInIframe() {
    const clampTop = Math.max(0, ViewportObserver.shared.scrollY);
    this.style.position = "absolute";
    this.style.top = `${clampTop}px`;
    this.style.bottom = "auto";
    this.style.height = `${ViewportObserver.shared.viewHeight}px`;
  }

  public appendCategory(category: WayfinderCategory) {
    this.list.appendChild(category);
  }

  public showWayfinder() {
    if (!this.list) return;

    this.wayfinderVisible = true;

    if (this.list.childElementCount > 0) {
      this.list.style.display = "block";
    }
    this.setScrollPosition(this.thumbValue);

    if (this.touchGuard) this.touchGuard.style.display = "block";
  }

  public hideWayfinder() {
    if (!this.list) return;

    this.wayfinderVisible = false;
    this.list.style.display = "none";

    if (this.touchGuard) this.touchGuard.style.display = "none";
  }

  private onThumbClickHandler = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    if (!this.list) return;

    if (!this.wayfinderVisible) {
      this.showWayfinder();
    }
  };

  private onAnchorRequest = (e: CustomEvent): void => {
    this.hideWayfinder();
  };

  private touchGuardHandler = (e: Event): void => {
    this.hideWayfinder();
  };

  connectedCallback() {
    if (this.parentElement) this.style.width = `${this.parentElement.offsetWidth}px`;

    this.addEventListener(C.ANCHOR_REQUEST_EVENT, this.onAnchorRequest as EventListener);

    if (!this.thumb) return;

    const eOps: any = { passive: false };
    if (window.navigator.pointerEnabled) this.thumb.addEventListener("pointerdown", this.startThumbDrag, eOps);
    else if (window.navigator.msPointerEnabled) this.thumb.addEventListener("MSPointerDown", this.startThumbDrag, eOps);
    else if ("ontouchstart" in window) this.thumb.addEventListener("touchstart", this.startThumbDrag, eOps);
    else this.thumb.addEventListener("mousedown", this.startThumbDrag, eOps);

    this.touchGuard.addEventListener("click", this.touchGuardHandler);
  }

  disconnectedCallback() {
    this.addEventListener(C.ANCHOR_REQUEST_EVENT, this.onAnchorRequest as EventListener);
    if (!this.thumb) return;

    const eOps: any = { passive: false };
    if (window.navigator.pointerEnabled) this.thumb.removeEventListener("pointerdown", this.startThumbDrag, eOps);
    else if (window.navigator.msPointerEnabled)
      this.thumb.removeEventListener("MSPointerDown", this.startThumbDrag, eOps);
    else if ("ontouchstart" in window) this.thumb.removeEventListener("touchstart", this.startThumbDrag, eOps);
    else this.thumb.removeEventListener("mousedown", this.startThumbDrag, eOps);

    this.touchGuard.removeEventListener("click", this.touchGuardHandler);

    if (this.hideWayfinderTimeout) clearTimeout(this.hideWayfinderTimeout);
  }

  public setAnchorPositions(anchors: { anchorId: string; percent: number }[]) {
    this.list.anchorPositions = anchors;
  }

  public setScrollPosition(percent: number) {
    this.thumbValue = percent;

    if (!this.thumb || !this.list || !this.thumbInnerTrack) return;

    const thumbTopAtEnd = this.thumbInnerTrack.offsetHeight;

    const lerpThumbTop = thumbTopAtEnd * percent;
    this.thumb.style.top = `${lerpThumbTop}px`;

    if (ViewportObserver.shared.iframed) this.positionInIframe();

    if (this.list.offsetHeight === 0) return;

    const containerTopAtEnd = this.offsetHeight - this.list.offsetHeight - 2 * WAYFINDER_MARGIN;

    const lerpTop = containerTopAtEnd * percent;
    this.list.style.top = `${WAYFINDER_MARGIN + lerpTop}px`;

    this.list.setScrollPosition(percent);
  }

  private handleThumbDrag = (e: Event) => {
    if (!this.scrubbing) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    this.updateThumbPosition(e);
  };

  private static preventDefault(e: Event) {
    e.preventDefault();
  }

  private updateThumbPosition = (e: Event) => {
    if (!this.scrubbing) return;

    let y = 0;
    if (e.type === "touchmove" || e.type === "touchstart") {
      let touch: Touch | null = null;
      let touches = (<TouchEvent>e).changedTouches;
      for (let i = 0, n = touches.length; i < n; ++i) {
        const candidate = touches[i];
        if (candidate.identifier === this.thumbPointerIdentifier) {
          touch = candidate;
          break;
        }
      }

      if (!touch) return;

      y = touch.clientY;
    } else if (
      e.type === "pointermove" ||
      e.type === "MSPointerMove" ||
      e.type === "pointerdown" ||
      e.type === "MSPointerDown"
    ) {
      const pointer = <PointerEvent>e;
      if (pointer.pointerId !== this.thumbPointerIdentifier) return;

      y = pointer.clientY;
    } else y = (<MouseEvent>e).clientY;

    const trackRect = this.thumbInnerTrack.getBoundingClientRect();
    const percent = Math.max(0, Math.min((y - trackRect.top - this.thumbDeltaY) / trackRect.height, 1));

    this.setScrollPosition(percent);

    const event = new CustomEvent(C.WAYFINDER_SCROLL_EVENT, {
      bubbles: true,
      detail: { percent, done: false },
    });
    this.dispatchEvent(event);

    e.preventDefault();
  };

  private endThumbDrag = (e: Event) => {
    if (!this.scrubbing) return;

    if (e.type === "touchend" || e.type === "touchcancel") {
      let touch: Touch | null = null;
      const touches = (<TouchEvent>e).changedTouches;
      for (let i = 0, n = touches.length; i < n; ++i) {
        const candidate = touches[i];
        if (candidate.identifier === this.thumbPointerIdentifier) {
          touch = candidate;
          break;
        }
      }
      if (!touch) return;
    } else if (e.type === "pointerup" || e.type === "pointercancel") {
      const pointer = <PointerEvent>e;
      if (pointer.pointerId !== this.thumbPointerIdentifier) return;

      this.thumb.releasePointerCapture(pointer.pointerId);
      this.thumbPointerIdentifier = undefined;
    } else if (e.type === "MSPointerUp" || e.type === "MSPointerCancel") {
      const pointer = <PointerEvent>e;
      if (pointer.pointerId !== this.thumbPointerIdentifier) return;

      (this.thumb as any).msReleasePointerCapture(pointer.pointerId);
      this.thumbPointerIdentifier = undefined;
    }

    this._thumbDragging = false;
    this.thumbPointerIdentifier = undefined;

    window.removeEventListener("selectstart", Wayfinder.preventDefault, true);

    const eOps: any = { capture: true, passive: false };
    if (window.navigator.pointerEnabled) {
      window.removeEventListener("pointermove", this.handleThumbDrag, eOps);
      window.removeEventListener("pointerup", this.endThumbDrag, eOps);
      window.removeEventListener("pointercancel", this.endThumbDrag, eOps);
    } else if (window.navigator.msPointerEnabled) {
      window.removeEventListener("MSPointerMove", this.handleThumbDrag, eOps);
      window.removeEventListener("MSPointerUp", this.endThumbDrag, eOps);
      window.removeEventListener("MSPointerCancel", this.endThumbDrag, eOps);
    } else if ("ontouchstart" in window) {
      window.removeEventListener("touchmove", this.handleThumbDrag, eOps);
      window.removeEventListener("touchend", this.endThumbDrag, eOps);
      window.removeEventListener("touchcancel", this.endThumbDrag, eOps);
    } else {
      (window as any).removeEventListener("mousemove", this.handleThumbDrag, eOps);
      (window as any).removeEventListener("mouseup", this.endThumbDrag, eOps);
    }

    e.preventDefault();
    e.stopImmediatePropagation();

    const scrollEvent = new CustomEvent(C.WAYFINDER_SCROLL_EVENT, {
      bubbles: true,
      detail: { percent: this.thumbValue, done: true },
    });
    this.dispatchEvent(scrollEvent);

    this.hideWayfinderTimeout = window.setTimeout(() => {
      this.hideWayfinderTimeout = null;
      this.hideWayfinder();
    }, WAYFINDER_HIDE_TIMER);
  };

  private startThumbDrag = (e: Event) => {
    if (this.scrubbing) return;

    if (this.hideWayfinderTimeout) {
      clearTimeout(this.hideWayfinderTimeout);
      this.hideWayfinderTimeout = null;
    }

    let y = 0;
    if (e.type === "touchstart") {
      const touch = (<TouchEvent>e).changedTouches[0];
      this.thumbPointerIdentifier = touch.identifier;
      y = touch.clientY;
    } else if (e.type === "MSPointerDown" || e.type === "pointerdown") {
      const pointer = <PointerEvent>e;
      this.thumbPointerIdentifier = pointer.pointerId;
      y = pointer.clientY;
    } else {
      const mouseEvent = <MouseEvent>e;
      if (mouseEvent.button !== 0) return;
      y = (<MouseEvent>e).clientY;
    }
    const thumbRect = this.thumb.getBoundingClientRect();
    this.thumbDeltaY = y - (thumbRect.top + thumbRect.height / 2);
    if (this.thumbDeltaY < -thumbRect.height / 2 || this.thumbDeltaY > thumbRect.height / 2) this.thumbDeltaY = 0;

    this.focus();
    this._thumbDragging = true;

    window.addEventListener("selectstart", Wayfinder.preventDefault, true);

    const eOps: any = { capture: true, passive: false };
    if (window.navigator.pointerEnabled) {
      const pointerEvent = <PointerEvent>e;
      this.thumb.setPointerCapture(pointerEvent.pointerId);
      window.addEventListener("pointermove", this.handleThumbDrag, eOps);
      window.addEventListener("pointerup", this.endThumbDrag, eOps);
      window.addEventListener("pointercancel", this.endThumbDrag, eOps);
    } else if (window.navigator.msPointerEnabled) {
      const pointerEvent = <PointerEvent>e;
      (this.thumb as any).msSetPointerCapture(pointerEvent.pointerId);
      window.addEventListener("MSPointerMove", this.handleThumbDrag, eOps);
      window.addEventListener("MSPointerUp", this.endThumbDrag, eOps);
      window.addEventListener("MSPointerCancel", this.endThumbDrag, eOps);
    } else if ("ontouchstart" in window) {
      window.addEventListener("touchmove", this.handleThumbDrag, eOps);
      window.addEventListener("touchend", this.endThumbDrag, eOps);
      window.addEventListener("touchcancel", this.endThumbDrag, eOps);
    } else {
      (window as any).addEventListener("mousemove", this.handleThumbDrag, eOps);
      (window as any).addEventListener("mouseup", this.endThumbDrag, eOps);
    }

    this.updateThumbPosition(e);
    this.showWayfinder();
  };
}
customElements.define("sfml-wayfinder", Wayfinder);



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/wayfinder/wayfinder.ts